import * as ImagePicker from 'expo-image-picker';
import { MB_customWindowForWeb } from '@/utils/general/custom-window-for-web';
import { isMobileApp } from '@/utils/general/device-info';
import { MB_fileManagement } from '@/utils/general/file-management';
import { MB_imageCompressor } from '@/utils/general/image-compressor';
import { MB_RESOURCE_PICKER_CONSTANTS } from '@/components/general/resource-picker';
import { RESOURCE_POOL_TYPE } from '../../../typesAndInterfaces/typesAndInterfaces';
import { BulkUploadFile, BulkUploadItem, BULK_UPLOAD_ITEM_STATUS } from './bulkUploadTypes';
import { nextBulkUploadItemId } from './useBulkUploadQueue';

const MAX_VIDEO_DURATION_SECONDS = 5 * 60;

const stripExtension = (fileName: string) => {
  const dotIndex = fileName.lastIndexOf('.');
  return dotIndex > 0 ? fileName.slice(0, dotIndex) : fileName;
};

const bytesToMb = (bytes: number) => Math.round(bytes / (1024 * 1024));

/**
 * Validates (videos) or compresses (images) the picked files and turns the valid ones
 * into review-list items. Mirrors the checks the single-upload picker applies: video
 * size/duration limits, image compression to the same max size. Invalid files are
 * skipped and reported via the returned notices.
 */
const processPickedFiles = async ({
  files,
  resourcePoolType,
}: {
  files: BulkUploadFile[];
  resourcePoolType: RESOURCE_POOL_TYPE;
}): Promise<{ items: BulkUploadItem[]; notices: string[] }> => {
  const items: BulkUploadItem[] = [];
  const notices: string[] = [];

  for (const file of files) {
    if (resourcePoolType === RESOURCE_POOL_TYPE.Video) {
      if (file.blob && file.blob.size > MB_RESOURCE_PICKER_CONSTANTS.MAX_VIDEO_SIZE) {
        notices.push(`${file.fileName}: larger than ${bytesToMb(MB_RESOURCE_PICKER_CONSTANTS.MAX_VIDEO_SIZE)} MB, skipped.`);
        continue;
      }
      if (!isMobileApp) {
        try {
          const duration = await MB_customWindowForWeb.getVideoDuration(file.uri);
          if (duration > MAX_VIDEO_DURATION_SECONDS) {
            notices.push(`${file.fileName}: longer than ${MAX_VIDEO_DURATION_SECONDS / 60} minutes, skipped.`);
            continue;
          }
        } catch {
          notices.push(`${file.fileName}: could not be read as a video, skipped.`);
          continue;
        }
      }
      items.push(createItem(file));
    } else {
      try {
        const compressed = await MB_imageCompressor({
          blobOrUrl: isMobileApp ? file.uri : file.blob,
          onProgress: () => {},
          maxSizeAfterCompression: MB_RESOURCE_PICKER_CONSTANTS.MAX_IMAGE_SIZE_AFTER_COMPRESSION,
          withBrowserImageCompression: !(file.blob?.type === 'image/jpeg' || file.blob?.type === 'image/jpg'),
          resultPathMobile: isMobileApp ? MB_fileManagement.getDirs().CacheDir + '/MB_ImageCompressor' : undefined,
        });
        items.push(createItem({ blob: compressed.blob, uri: compressed.uri, fileName: file.fileName }));
      } catch {
        notices.push(`${file.fileName}: could not be compressed below ${bytesToMb(MB_RESOURCE_PICKER_CONSTANTS.MAX_IMAGE_SIZE_AFTER_COMPRESSION)} MB, skipped.`);
      }
    }
  }

  return { items, notices };
};

const createItem = (file: BulkUploadFile): BulkUploadItem => ({
  id: nextBulkUploadItemId(),
  file,
  name: stripExtension(file.fileName).trim(),
  tags: [],
  status: BULK_UPLOAD_ITEM_STATUS.ready,
  progress: 0,
  isDuplicateOnServer: false,
  showNameError: false,
  showTagsError: false,
});

/**
 * Multi-select picker for mobile (the web flow uses the dropzone instead).
 */
const pickFilesOnMobile = async ({ resourcePoolType, selectionLimit }: { resourcePoolType: RESOURCE_POOL_TYPE; selectionLimit: number }): Promise<BulkUploadFile[]> => {
  const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
  if (!permission.granted) {
    return [];
  }

  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: resourcePoolType === RESOURCE_POOL_TYPE.Image ? ['images'] : ['videos'],
    allowsMultipleSelection: true,
    selectionLimit,
    quality: 1,
  });

  if (result.canceled || !result.assets) {
    return [];
  }

  return result.assets
    .filter(asset => !asset.duration || asset.duration / 1000 <= MAX_VIDEO_DURATION_SECONDS)
    .map(asset => ({
      blob: undefined,
      uri: asset.uri,
      fileName: asset.fileName ?? asset.uri.split('/').pop() ?? 'untitled',
    }));
};

export { processPickedFiles, pickFilesOnMobile, stripExtension, MAX_VIDEO_DURATION_SECONDS };
